import { createFileRoute, redirect } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ShieldX, Briefcase } from 'lucide-react';
import * as React from 'react';
import { authClient, signOut } from '@/lib/auth-client';
import { api } from '@/lib/api-client';
import { AuthLayout } from '@/components/auth/auth-layout';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/deactivated')({
  beforeLoad: async () => {
    const session = await authClient.getSession();
    if (!session.data) {
      throw redirect({ to: '/' });
    }
  },
  component: DeactivatedPage,
});

function DeactivatedPage() {
  const { t } = useTranslation();
  const [isLawyer, setIsLawyer] = React.useState<boolean | null>(null);

  React.useEffect(() => {
    api
      .get<{ data: { deactivatedAsLawyer?: boolean } }>('/users/me/onboarding')
      .then((res) => {
        setIsLawyer(res.data?.deactivatedAsLawyer === true);
      })
      .catch(() => {
        setIsLawyer(false);
      });
  }, []);

  const handleLogout = async () => {
    try {
      await signOut();
    } catch {
      // Force redirect even if signOut fails
    }
    window.location.href = '/';
  };

  // Show nothing until we know the variant
  if (isLawyer === null) {
    return null;
  }

  const title = isLawyer ? t('deactivated.lawyerTitle') : t('deactivated.title');
  const description = isLawyer ? t('deactivated.lawyerDescription') : t('deactivated.description');

  return (
    <AuthLayout title={title} description={description}>
      <div className="flex flex-col items-center gap-4 py-6">
        <div
          className={`flex size-16 items-center justify-center rounded-full ${
            isLawyer ? 'bg-blue-100 dark:bg-blue-950/50' : 'bg-amber-100 dark:bg-amber-950/50'
          }`}
        >
          {isLawyer ? (
            <Briefcase className="size-8 text-blue-600 dark:text-blue-400" />
          ) : (
            <ShieldX className="size-8 text-amber-600 dark:text-amber-400" />
          )}
        </div>
        {isLawyer && (
          <p className="text-center text-sm text-muted-foreground">{t('deactivated.lawyerHint')}</p>
        )}
      </div>
      <Button className="w-full cursor-pointer" size="lg" onClick={handleLogout}>
        {t('deactivated.logout')}
      </Button>
    </AuthLayout>
  );
}
